import { AfterViewChecked, Component, ElementRef, HostListener, NgZone, OnDestroy, OnInit, viewChild, ViewChild } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { timeout } from "rxjs/operators";

import {
    ConversationGroup,
    ConversationRow,
    ConversationStoreService,
    UNTITLED_CONVERSATION
} from "../../services/conversation-store.service";
import { UserService } from "../../services/user.service";
import { SseEvent, SseParser } from "../../services/sse-parser";
import { AgentKey, ChatTurn, HistoryDetailResponse, HistoryListResponse } from "../../model/uchat.model";
import { ConversationSidebarComponent } from "./conversation-sidebar/conversation-sidebar.component";
import { ChatTurnComponent } from "./chat-turn/chat-turn.component";
import { ApiRequestService } from "src/app/services/api-request.service";
import { AgentDefinition } from "src/app/model/agentDefinition";
import { StreamContext } from "src/app/model/streamContext";
import { AgentCatalogueService } from "src/app/services/agent-catalogue.service";
import { AppConfigService } from "src/app/app-config.service";
import { UChatFeedbackPayload } from "src/app/model/uchatFeedbackPayload";

// How long to wait for an expired token to refresh before sending anyway.
const TOKEN_REFRESH_TIMEOUT_MS = 8000;

/**
 * How long a stream may deliver nothing at all before it is treated as wedged.
 */
const STREAM_IDLE_TIMEOUT_MS = 60000;

const STREAM_IDLE_ERROR = "uchat-stream-idle";

/**
 * How close to the bottom of the thread counts as "following along".
 */
const SCROLL_STICK_PX = 100;

/**
 * Space left below the disclaimer when the page, not the thread, is scrolled.
 */
const COMPOSER_GAP_PX = 16;

@Component({
    selector: '[app-agent-chat]',
    standalone: true,
    imports: [
    FormsModule,
    ConversationSidebarComponent,
    ChatTurnComponent,
    ReactiveFormsModule
],
    templateUrl: './agent-chat.component.html',
    styleUrl: './agent-chat.component.scss'
})
export class AgentChatComponent implements OnInit, OnDestroy, AfterViewChecked {

    public agents: AgentDefinition[];

    public isPositive = 1;
    public positiveRating = "up";
    public isNegative = -1;
    public negativeRating = "down";

    
    // Sidebar rows, already grouped and filtered.
    public groupedRows: ConversationGroup[] = [];

    // Sidebar
    public query: string = "";
    public openMenu: string | null = null;
    public sideOpen: boolean = false;
    public sideCollapsed: boolean = false;
    public renamingId: string | null = null;
    public renameText: string = "";
    public listError: boolean = false;
    public pendingDeleteId: string | null = null;
    public deleteError: string = "";

    /**
     * The kebab that opened the currently open menu, so closing the menu can
     * put focus back where it came from.
     */
    private menuTrigger: HTMLElement | null = null;

    // Thread (conversation) parameters
    public activeId: string | null = null;
    public draftAgent: AgentKey | null = null;

    /**
     * The open conversation's agent, held directly rather than looked up from
     * the sidebar row.
     */
    public activeAgent: AgentKey | null = null;

    public turns: ChatTurn[] = [];

    //Bound to a spinner in the thread, but only while there are no turns to read.
    public loadingThread: boolean = false;
    public pending: boolean = false;

    // Resolves when the current stream finishes. Tests await it; nothing else reads it. 
    public inFlight: Promise<void> = Promise.resolve();

    /**
     * Aborts the transport for the stream that is running now.
     */
    private streamAbort: AbortController | null = null;

    /**
     * ID for if in current instance. New instances increase this value by one.
     */
    private sendGeneration: number = 0;

    private stopRequested: boolean = false;

    private scrollTimer: any = null;

    private backToTopFrame: number | null = null;
    private forceScroll: boolean = false;

    /**
     * Scrollbar logic variables
     */
    private stickToBottom: boolean = true;
    private lastScrollTop: number = 0;

    public loadError: string = "";
    public streamError: string = "";

    // Rating failures, keyed by turn id, shown under that turn's feedback box.
    public feedbackErrors: { [turnId: string]: string } = {};

    // Turns whose rating is still being saved.
    public feedbackSending: { [turnId: string]: boolean } = {};

    /**
     * Short, deliberate screen-reader announcements.
     */
    public liveStatus: string = "";

    /** Composer */
    public userInput: string = "";
    public readonly maxLength: number = 300;

    public showDisclaimerContent: boolean = false;
    @ViewChild('threadWrap') private threadWrap: ElementRef<HTMLElement>;

    @ViewChild('composer') private composer: ElementRef<HTMLDivElement>;
    @ViewChild('composerInput') private composerInput: ElementRef<HTMLTextAreaElement>;
    @ViewChild("disclaimerWrapper") private disclaimerWrapper: ElementRef<HTMLDivElement>;
    @ViewChild("disclaimerContent") private disclaimerContent: ElementRef<HTMLDivElement>;

    constructor(
        private store: ConversationStoreService,
        private api: ApiRequestService,
        private userService: UserService,
        private agentCatalogueService: AgentCatalogueService,
        private appConfig: AppConfigService,
        private zone: NgZone
    ) {}

    public ngOnInit(): void {
        this.agents = this.agentCatalogueService.getAgentCatalogue();
        this.loadConversations();

        if(this.store.hasChatHistoryId()){
            this.activeId = this.store.getHistoryId();
            this.openConversation(this.activeId);
        }
    }

    public ngOnDestroy(): void {
        this.abortStream();
        if ( this.scrollTimer !== null ) {
            clearTimeout(this.scrollTimer);
            this.scrollTimer = null;
        }
        if ( this.backToTopFrame !== null ) {
            cancelAnimationFrame(this.backToTopFrame);
            this.backToTopFrame = null;
        }
        // #backtotop lives in the app shell, so hand it back its default position.
        document.getElementById("backtotop")?.style.removeProperty("bottom");
    }

    public loadConversations(): void {
        this.listError = false;
        this.api.listConversations().subscribe({
            next: (response: HistoryListResponse) => {
                this.store.setFromResponse(response);
                this.refreshGroups();
            },
            error: () => {
                this.listError = true;
            }
        });
    }

    /**
     * Recompute the sidebar. Called on load, on search, and after any row change.
     */
    private refreshGroups(): void {
        this.groupedRows = this.store.groups(this.query, Date.now());
    }

    public onQueryChange(): void {
        this.refreshGroups();
    }

    /**
     * 'event' is optional so callers that have no DOM event -- tests, and
     * anything programmatic -- still work; they simply get no focus return.
     * 
     * @param historyId 
     * @param event 
     * @returns 
     */
    public toggleMenu( historyId: string, event?: Event ): void {
        if ( this.openMenu === historyId ) {
            this.closeMenu();
            return;
        }
        this.openMenu = historyId;
        this.pendingDeleteId = null;
        this.menuTrigger = event != null && event.currentTarget instanceof HTMLElement
            ? event.currentTarget
            : null;
        this.scrollMenuIntoRoom();
    }

    /**
     * Scroll the row so its popup will fit before the popup opens.
     */
    private scrollMenuIntoRoom(): void {
        if ( this.menuTrigger === null ) {
            return;
        }
        let row = this.menuTrigger.closest(".conv");
        if ( row !== null ) {
            row.scrollIntoView( { block: "nearest" } );
        }
    }

    public closeMenu( returnFocus: boolean = true ): void {
        let wasOpen = this.openMenu !== null;
        let trigger = this.menuTrigger;

        this.openMenu = null;
        this.pendingDeleteId = null;
        this.menuTrigger = null;

        if ( returnFocus && wasOpen && trigger !== null && trigger.isConnected ) {
            trigger.focus();
        }
    }

    /**
     * Escape closes the kebab menu, which also disarms a pending delete.
     */
    @HostListener("document:keydown.escape")
    public onEscape(): void {
        let menuWasOpen = this.openMenu !== null;
        this.closeMenu();
        if ( !menuWasOpen ) {
            this.sideOpen = false;
        }
    }

    public startRename( row: ConversationRow ): void {
        this.renamingId = row.historyId;
        this.renameText = row.title;
        this.openMenu = null;
        this.focusAfterRender("rename_" + row.historyId);
    }

    public cancelRename(): void {
        this.renamingId = null;
        this.renameText = "";
    }

    /**
     * Apply the title locally first, then tell the backend. The rename route is
     * an assumed contract, so a failure degrades to a local-only rename rather
     * than losing the user's edit.
     */
    public commitRename(): void {
        let historyId = this.renamingId;
        let title = this.renameText.trim();

        if ( historyId === null ) {
            return;
        }
        if ( title === "" ) {
            this.cancelRename();
            return;
        }
        this.store.applyTitle(historyId, title);
        this.refreshGroups();
        this.renamingId = null;
        this.renameText = "";
        this.api.renameConversation(historyId, title).subscribe({
            next: () => {},
            error: () => {
                // Local override already applied; nothing further to do.
            }
        });
    }

    /** Two-step delete: the kebab menu asks, this arms it. No native dialog. */
    public requestDelete( row: ConversationRow ): void {
        this.pendingDeleteId = row.historyId;
        this.focusAfterRender("confirmDelete_" + row.historyId);
    }

    /**
     * Focus an element that does not exist yet.
     */
    private focusAfterRender( elementId: string ): void {
        setTimeout( () => {
            let element = document.getElementById(elementId);
            if ( element !== null ) {
                element.focus();
            }
        }, 0 );
    }

    public confirmDelete( row: ConversationRow ): void {
        let historyId = row.historyId;

        let title = row.title;

        this.store.remove(historyId);
        this.refreshGroups();
        this.openMenu = null;
        this.pendingDeleteId = null;
        this.deleteError = "";
        if ( this.activeId === historyId ) {
            this.newChat();
        }
        this.liveStatus = "Conversation deleted";
        this.api.deleteConversation(historyId).subscribe({
            next: () => {},
            error: () => {
                this.deleteError = "This conversation could not be deleted. It has been restored to your list.";
                this.liveStatus = this.deleteError;
                this.restoreAfterFailedDelete(historyId, title);
            }
        });
    }

    /**
     * Re-list after a failed delete, then put the user's title back on the restored row.
     * @param historyId 
     * @param title 
     */
    private restoreAfterFailedDelete( historyId: string, title: string ): void {
        this.listError = false;
        this.api.listConversations().subscribe({
            next: (response: HistoryListResponse) => {
                this.store.setFromResponse(response);
                if ( this.store.row(historyId) !== null ) {
                    this.store.applyTitle(historyId, title);
                }
                this.refreshGroups();
            },
            error: () => {
                this.listError = true;
            }
        });
    }

    public newChat(): void {
        this.abortStream();
        this.activeId = null;
        this.store.removeHistoryId();
        this.draftAgent = null;
        this.activeAgent = null;
        this.turns = [];
        this.pending = false;
        this.loadError = "";
        this.streamError = "";
        this.feedbackErrors = {};
        this.feedbackSending = {};
        this.openMenu = null;
        this.sideOpen = false;
        // A half-typed question belongs to the conversation it was typed in.
        this.userInput = "";
        this.resetComposerHeight();
    }

    /**
     * Choose the service area for a new chat.
     */
    public pickAgent( key: AgentKey ): void {
        if ( this.pending ) {
            return;
        }
        this.draftAgent = key;
        this.scrollToBottom(true);
    }

    public openConversation( historyId: string ): void {
        this.abortStream();
        this.pending = false;

        this.activeId = historyId;
        this.store.setHistoryId(this.activeId);
        this.draftAgent = null;
        this.openMenu = null;
        this.sideOpen = false;
        this.loadError = "";
        this.streamError = "";
        this.feedbackErrors = {};
        this.feedbackSending = {};
        this.userInput = "";
        this.resetComposerHeight();

        let row = this.store.row(historyId);
        this.activeAgent = row === null ? null : row.agent;

        let cached = this.store.cachedTurns(historyId);
        this.turns = cached === null ? [] : cached;
        let hadCachedTurns = this.turns.length > 0;
        this.loadingThread = true;
        this.liveStatus = "Loading conversation";

        this.api.getConversation(historyId).subscribe({
            next: (detail: HistoryDetailResponse) => {
                this.turns = this.store.toChatTurns(detail.turns);
                this.store.cacheTurns(historyId, this.turns);
                if ( detail.agent != null ) {
                    this.activeAgent = detail.agent;
                }

                if ( this.store.applyServerTitle(historyId, detail.title) ) {
                    this.refreshGroups();
                }
                this.loadingThread = false;
                this.liveStatus = "Conversation loaded";
                this.scrollToBottom(true);
            },
            error: () => {
                this.loadingThread = false;

                this.loadError = hadCachedTurns
                    ? "Showing a saved copy — this conversation could not be refreshed."
                    : "This conversation could not be loaded. Please try again.";
                this.liveStatus = this.loadError;
            }
        });
    }

    public openInNewChat( row: ConversationRow ): void {
        this.newChat();
        this.draftAgent = row.agent;
    }

    public currentAgent(): AgentKey | null {
        return this.activeId !== null ? this.activeAgent : this.draftAgent;
    }

    public currentDefinition(): AgentDefinition | null {
        let key = this.currentAgent();
        return key === null ? null : this.agentCatalogueService.agentDefinition(key);
    }

    public agentLabelFor( key: AgentKey ): string {
        return this.agentCatalogueService.agentLabel(key);
    }

    public currentAgentLabel(): string {
        let definition = this.currentDefinition();
        if ( definition !== null ) {
            return definition.label;
        }
        let key = this.currentAgent();
        return key === null ? "No agent selected" : this.agentCatalogueService.agentLabel(key);
    }

    public currentTitle(): string {
        if ( this.activeId === null ) {
            return "New chat";
        }
        let row = this.store.row(this.activeId);
        return row === null ? "Conversation" : row.title;
    }

    public get displayName(): string {
        return this.userService.getUserName();
    }

    public placeholder(): string {
        let definition = this.currentDefinition();
        if ( definition !== null ) {
            return definition.placeholder;
        }

        return this.currentAgent() !== null
            ? "Type your message ..."
            : "Choose a service area above to start a conversation.";
    }

    public canSend(): boolean {
        let text = this.userInput.trim();
        return this.currentAgent() !== null
            && !this.pending
            && text.length > 0
            && text.length <= this.maxLength;
    }

    public onComposerEnter( event: Event ): void {
        if ( (event as KeyboardEvent).isComposing ) {
            return;
        }
        event.preventDefault();
        this.send();
    }

    /**
     * Grow the composer to fit what has been typed.
     */
    public autoGrowComposer(): void {
        if ( this.composerInput == null ) {
            return;
        }
        let element = this.composerInput.nativeElement;

        element.style.height = "auto";

        let border = element.offsetHeight - element.clientHeight;
        element.style.height = (element.scrollHeight + border) + "px";
    }

    /**
     * Put the composer back to one row.
     */
    private resetComposerHeight(): void {
        if ( this.composerInput == null ) {
            return;
        }
        this.composerInput.nativeElement.style.height = "";
    }

    public send(): void {
        if ( !this.canSend() ) {
            return;
        }
        let agent = this.currentAgent();
        if ( agent === null ) {
            return;
        }
        this.abortStream();

        let text = this.userInput.trim().slice(0, this.maxLength);
        this.userInput = "";
        this.resetComposerHeight();
        this.streamError = "";
        this.stopRequested = false;

        this.turns.push( {
            role: "user",
            content: text,
            at: new Date().toISOString(),
            sources: [],
            streaming: false,
            failed: false
        } );
        this.turns.push( {
            role: "assistant",
            content: "",
            at: new Date().toISOString(),
            sources: [],
            streaming: true,
            failed: false
        } );
        this.pending = true;
        this.scrollToBottom(true);

        this.inFlight = this.streamReply(text, agent, this.activeId);
    }

    /**
     * POST the message and fold the SSE events into the pending assistant turn.
     */
    private async streamReply( text: string, agent: AgentKey, historyId: string | null ): Promise<void> {
        let thread = this.turns;
        let turn = thread[thread.length - 1];
        let generation = ++this.sendGeneration;

        let controller = new AbortController();
        this.streamAbort = controller;

        let context: StreamContext = { agent: agent, sawDone: false, warnedNoText: false };
        let idle = false;

        let body = JSON.stringify( {
            message: text,
            history_id: historyId,
            agent: agent
        } );

        try {
            await this.ensureFreshToken();

            let response = await this.openStream(body, controller.signal);
            if ( response == null || !response.ok || response.body == null ) {
                throw new Error("stream rejected");
            }
            let parser = new SseParser();
            let decoder = new TextDecoder();
            let reader = response.body.getReader();
            let result = await this.readChunk(reader, controller);

            while ( !result.done ) {
                if ( this.abandoned(thread) || !this.owns(generation) ) {
                    this.abandonStream(generation);
                    return;
                }
                let events:Array<SseEvent> = parser.push( decoder.decode(result.value, { stream: true }) );
                events.forEach( (event: SseEvent) => this.applyEvent(event, turn, context) );
                this.scrollToBottom();
                result = await this.readChunk(reader, controller);
            }
            if ( this.abandoned(thread) || !this.owns(generation) ) {
                this.abandonStream(generation);
                return;
            }
            parser.flush().forEach( (event: SseEvent) => this.applyEvent(event, turn, context) );


            if ( turn.content === "" ) {
                turn.content = "No answer came back. Please try your question again.";
                turn.failed = true;
                this.streamError = "Something went wrong answering that. Please try again.";
            }
        } catch ( e ) {
            if ( this.abandoned(thread) || !this.owns(generation) ) {
                this.abandonStream(generation);
                return;
            }
            idle = e instanceof Error && e.message === STREAM_IDLE_ERROR;
            turn.failed = true;
            if ( turn.content === "" ) {
                turn.content = this.stopRequested
                    ? "Stopped before the agent answered."
                    : "I could not reach the agent. Please try your question again.";
            }
            if ( !this.stopRequested ) {
                // Stopping is the user's own doing, not an error to report.
                this.streamError = idle
                    ? "The agent stopped responding. Please try again."
                    : "The connection to the agent was interrupted. Please try again.";
            }

        }

        turn.streaming = false;

        let incomplete = !context.sawDone && !turn.failed;
        turn.incomplete = incomplete;

        if ( this.streamAbort === controller ) {
            this.streamAbort = null;
        }
        if ( !this.owns(generation) ) {
            return;
        }

        this.pending = false;

        if ( incomplete ) {
            this.streamError = "The answer may be incomplete. Check your conversation list for the full reply.";
            if ( this.activeId === null ) {
                this.loadConversations();
            }
        }

        if ( this.stopRequested ) {
            this.liveStatus = "Answer stopped";
        } else if ( turn.failed ) {
            this.liveStatus = this.streamError;
        } else if ( incomplete ) {
            this.liveStatus = "Answer may be incomplete";
        } else {
            this.liveStatus = "Answer ready";
        }

        if ( this.activeId !== null ) {
            this.store.cacheTurns(this.activeId, this.turns);
        }
        this.scrollToBottom();
    }

    /**
     * One read(), given up on if nothing arrives before the idle deadline.
     */
    private readChunk(
        reader: ReadableStreamDefaultReader<Uint8Array>,
        controller: AbortController
    ): Promise<ReadableStreamReadResult<Uint8Array>> {
        return new Promise( (resolve, reject) => {
            let timer = setTimeout( () => {
                controller.abort();
                reject( new Error(STREAM_IDLE_ERROR) );
            }, STREAM_IDLE_TIMEOUT_MS );
            reader.read().then(
                (result: ReadableStreamReadResult<Uint8Array>) => {
                    clearTimeout(timer);
                    resolve(result);
                },
                (error: any) => {
                    clearTimeout(timer);
                    reject(error);
                }
            );
        } );
    }

    /**
     * Stop the current answer at the user's request.
     */
    public stopStreaming(): void {
        if ( !this.pending ) {
            return;
        }
        this.stopRequested = true;

        let turn = this.turns.length > 0 ? this.turns[this.turns.length - 1] : null;
        if ( turn !== null && turn.streaming ) {
            turn.streaming = false;
            turn.failed = true;
            if ( turn.content === "" ) {
                turn.content = "Stopped before the agent answered.";
            }
        }
        this.pending = false;
        this.liveStatus = "Answer stopped";
        this.abortStream();
    }

    /** Tear down the transport of whatever stream is running. */
    private abortStream(): void {
        if ( this.streamAbort !== null ) {
            this.streamAbort.abort();
            this.streamAbort = null;
        }
    }

    /**
     * True once the thread this stream was started in is no longer on screen.
     */
    private abandoned( thread: ChatTurn[] ): boolean {
        return this.turns !== thread;
    }

    /** Whether this stream is still the newest send, and so owns shared state. */
    private owns( generation: number ): boolean {
        return this.sendGeneration === generation;
    }

    /**
     * Stop tracking an abandoned stream.
     * @param generation 
     */
    private abandonStream( generation: number ): void {
        if ( this.owns(generation) ) {
            this.pending = false;
        }
    }

    /**
     * Refresh an expired CAS token before the stream headers are built.
     */
    private ensureFreshToken(): Promise<void> {
        // This will actually refresh the cookie in the user service if it is gone. 
        this.userService.getPawsCookie();
        if ( !this.userService.isTokenExpired() ) {
            return Promise.resolve();
        }
        return new Promise<void>( (resolve) => {
            this.userService.refreshUserTokenAwaitable()
                .pipe( timeout(TOKEN_REFRESH_TIMEOUT_MS) )
                .subscribe({
                    next: () => resolve(),
                    error: () => resolve(),
                    complete: () => resolve()
                });
        } );
    }

    protected openStream( body: string, signal: AbortSignal ): Promise<Response> {
        return fetch( this.api.streamUrl(), {
            method: 'POST',
            headers: this.api.streamHeaders(),
            credentials: 'include',
            body: body,
            signal: signal
        } );
    }

    /**
     * Fold one SSE event into the in-flight assistant turn. 
     * @param event 
     * @param turn 
     * @param context 
     * @returns 
     */
    private applyEvent( event: SseEvent, turn: ChatTurn, context: StreamContext ): void {
        // A dataless event arrives as data: null.
        let data = event.data == null ? {} : event.data;
        if ( event.event === "delta" ) {
            let text = typeof data === "string" ? data : data.text;
            if ( text != null ) {
                turn.content += text;
                return;
            }
            if ( !context.warnedNoText ) {
                context.warnedNoText = true;
                console.warn(
                    "uchat: delta event carried no usable text; payload keys: "
                    + Object.keys(data).join(",")
                );
            }
            return;
        }
        if ( event.event === "sources" ) {
            let sources = Array.isArray(data) ? data : data.sources;
            if ( Array.isArray(sources) ) {
                let seenUrls = new Set<string>();

                turn.sources = sources.filter( (source: any) => source != null
                    && typeof source === "object"
                    && typeof source.title === "string"
                    && typeof source.url === "string"
                    && source.title.trim() !== "" 
                );
                turn.sources = turn.sources.filter((source:any)=> {
                    if(seenUrls.has(source.url)){
                        return false;
                    } 
                    seenUrls.add(source.url);
                    return true;
                });
            }
            return;
        }
        if ( event.event === "done" ) {
            context.sawDone = true;
            // The id the answer was stored under; needed to rate it.
            if ( data != null && typeof data.turn_id === "string" && data.turn_id !== "" ) {
                turn.id = data.turn_id;
            }
            this.registerConversation(data, context.agent);
            return;
        }
        if ( event.event === "error" ) {
            turn.failed = true;
            this.streamError = "Something went wrong answering that. Please try again.";
        }
    }

   /**
    * The 'done' event carries the history id the backend used -- which is how a
    * brand-new chat learns its id and becomes a row in the sidebar.
    * @param data 
    * @param agent 
    * @returns 
    */
    private registerConversation( data: any, agent: AgentKey ): void {
        let historyId = data == null ? null : data.history_id;
        if ( historyId == null || historyId === "" ) {
            return;
        }
        let existing = this.store.row(historyId);
        if ( existing === null ) {
            this.store.prepend( {
                historyId: historyId,
                title: data.title != null && data.title !== "" ? data.title : this.titleFrom(this.turns),
                serverTitle: data.title != null && data.title !== "" ? data.title : null,
                agent: agent,
                modifiedAt: Date.now()
            } );
        } else {
            this.store.applyServerTitle(historyId, data.title);
            this.store.touch(historyId, Date.now());
        }
        this.activeId = historyId;
        this.store.setHistoryId(this.activeId);
        this.activeAgent = agent;
        this.draftAgent = null;
        this.refreshGroups();
    }

    private titleFrom( turns: ChatTurn[] ): string {
        let first = turns.find( (t: ChatTurn) => t.role === "user" );
        if ( first === undefined ) {
            return UNTITLED_CONVERSATION;
        }
        let text = first.content.replace(/\s+/g, " ").trim();
        return text.length > 42 ? text.slice(0, 42).trim() + "…" : text;
    }

    /**
     * Scroll the thread to the newest turn.
     * @param force
     */
    public scrollToBottom( force: boolean = false ): void {
        if ( force ) {
            this.forceScroll = true;
        }
        if ( this.scrollTimer !== null ) {
            return;
        }
        this.scrollTimer = setTimeout( () => {
            this.scrollTimer = null;
            let forced = this.forceScroll;
            this.forceScroll = false;
            if ( this.threadWrap == null ) {
                return;
            }
            if ( !forced && !this.stickToBottom ) {
                return;
            }
            let scrollContainerElement = this.scrollContainer();
            if ( scrollContainerElement === null ) {
                return;
            }
            this.stickToBottom = true;
            scrollContainerElement.scroll( { top: this.followTarget(scrollContainerElement), behavior: 'smooth' } );
        }, 120 );
    }

    /**
     * Show/hide the PAWS Agent Disclaimer. When opening, scroll so the text is visible.
     */
    public toggleDisclaimer(): void {
        this.showDisclaimerContent = !this.showDisclaimerContent;
        if ( !this.showDisclaimerContent ) {
            return;
        }
        setTimeout( () => {
            this.disclaimerContent?.nativeElement.scrollIntoView( { behavior: 'smooth', block: 'nearest' } );
        } );
    }

    /**
     * The element that actually scrolls the thread: .thread-wrap when the layout
     * bounds its height, otherwise the page itself.
     */
    private scrollContainer(): HTMLElement | null {
        if ( this.threadWrap == null ) {
            return null;
        }
        let element = this.threadWrap.nativeElement;
        if ( element.scrollHeight > element.clientHeight ) {
            return element;
        }
        return document.scrollingElement as HTMLElement | null;
    }

    /**
     * Gets the scroll bar to scroll to certain position. In this case
     * the location of where the PAWS Agent Disclaimer is
     * @param scrollContainerElement - The scroll container element
     * @returns 
     */
    private followTarget( scrollContainerElement: HTMLElement ): number {
        let max = scrollContainerElement.scrollHeight - scrollContainerElement.clientHeight;
        if ( scrollContainerElement === this.threadWrap.nativeElement ) {
            return max;
        }
        let anchor = this.disclaimerWrapper?.nativeElement ?? this.composerInput?.nativeElement;
        if ( anchor == null ) {
            return max;
        }
        let anchorBottom = anchor.getBoundingClientRect().bottom;
        let target = scrollContainerElement.scrollTop + anchorBottom + COMPOSER_GAP_PX - window.innerHeight;
        return Math.max(0, Math.min(max, target));
    }

    /**
     * Scrolling assistant. When a response is made it will scroll to the bottom, but if a 
     * user scrolls up the automatically scrolling will stop.
     */
    @HostListener("window:scroll")
    public onThreadScroll(): void {
        let element = this.scrollContainer();
        if ( element === null ) {
            return;
        }
        let top = element.scrollTop;
        let nearBottom = this.followTarget(element) - top <= SCROLL_STICK_PX;
        if ( top < this.lastScrollTop ) {
            this.stickToBottom = nearBottom;
        } else if ( nearBottom ) {
            this.stickToBottom = true;
        }
        this.lastScrollTop = top;
        this.scheduleBackToTopPosition();
    }

    @HostListener("window:resize")
    public onWindowResize(): void {
        this.scheduleBackToTopPosition();
    }

    // Rating controls grow when a thumb is picked, without any scroll or resize.
    public ngAfterViewChecked(): void {
        this.scheduleBackToTopPosition();
    }

    /**
     * Helps ensure back to top button does not collide with chat box area
     */
    private scheduleBackToTopPosition(): void {
        if ( this.backToTopFrame !== null ) {
            return;
        }
        this.zone.runOutsideAngular(() => {
            this.backToTopFrame = requestAnimationFrame(() => {
                this.backToTopFrame = null;
                this.positionBackToTop();
            });
        });
    }

    /**
     * Lift the back-to-top button above the composer, the disclaimer and any
     * turn's rating controls, only while it would overlap them.
     */
    private positionBackToTop(): void {
        let backToTop = document.getElementById("backtotop");
        let button = backToTop?.querySelector("button");
        let composer = this.composer?.nativeElement;
        if ( backToTop == null || button == null || composer == null ) {
            return;
        }
        let obstacles: Element[] = [composer];
        if ( this.disclaimerWrapper != null ) {
            obstacles.push(this.disclaimerWrapper.nativeElement);
        }
        if ( this.threadWrap != null ) {
            obstacles.push(...Array.from(this.threadWrap.nativeElement.querySelectorAll(".reviewWrapper")));
        }

        backToTop.style.removeProperty("bottom");
        let padding = button.getBoundingClientRect().bottom - backToTop.getBoundingClientRect().bottom;

        // Each lift only moves up, so one pass per obstacle is enough to clear a stack of them.
        for ( let i = 0; i < obstacles.length; i++ ) {
            let buttonRect = button.getBoundingClientRect();
            let blocking = obstacles
                .map((el: Element) => el.getBoundingClientRect())
                .filter((rect: DOMRect) => rect.left < buttonRect.right && rect.right > buttonRect.left
                    && rect.top < buttonRect.bottom && rect.bottom > buttonRect.top);
            if ( blocking.length === 0 ) {
                return;
            }
            let top = Math.min(...blocking.map((rect: DOMRect) => rect.top));
            let bottom = window.innerHeight - top + COMPOSER_GAP_PX + padding;
            if ( bottom + buttonRect.height > window.innerHeight ) {
                return;
            }
            backToTop.style.bottom = `${bottom}px`;
        }
    }

    public hasAccessToAgent(roles:Array<string>):boolean{
        let hasRole:boolean = false;
        roles.forEach((role)=>{
            if(this.userService.hasRole(role)){
                hasRole = true;
            }
        });
        return  this.appConfig.getIsUchatEnabled() && ( this.userService.isLoggedIn() && hasRole);
    }

    public accessToChatToAgent():boolean{
        if(this.activeAgent){
            let currentAgent = this.agentCatalogueService.agentDefinition(this.activeAgent);
            if(currentAgent){
                return this.hasAccessToAgent(currentAgent.roles);
            }
        } 
        else if(this.draftAgent){
            let currentAgent = this.agentCatalogueService.agentDefinition(this.draftAgent);
            if(currentAgent){
                return this.hasAccessToAgent(currentAgent.roles);
            }
        }
        return false;

    }

    public canViewAgent(){
        let roles = this.appConfig.getUChatAccessRoles();
        let hasRole = false;
        roles.forEach((role)=>{
            if(this.userService.hasRole(role)){
                hasRole = true;
            }
        });
        return this.appConfig.getIsUchatEnabled() && ( this.userService.isLoggedIn() && hasRole);
    }


    get userInputLength():number{
        return this.userInput.length;
    }

    get isSoftLimitExceeded(): boolean {
        return this.userInputLength > this.maxLength
    }

    public sendFeedback(toSend:UChatFeedbackPayload){
        if ( this.isFeedbackSending(toSend.turnId) ) {
            return;
        }
        this.clearFeedbackError(toSend.turnId);
        this.feedbackSending = {...this.feedbackSending, [toSend.turnId]: true};
        this.api.addRating(this.activeId, toSend.turnId, toSend.rating, toSend.comment).subscribe({
            next: ()=>{
                this.turns = this.turns.map(turn =>
                    turn.id == toSend.turnId ? {...turn, rating:this.convertRatingStringToRatingNumber(toSend.rating)}: turn
                );
                this.liveStatus = "Feedback sent";
                this.clearFeedbackSending(toSend.turnId);
            },
            error:()=>{
                let message = "Your feedback could not be sent. Please try again.";
                this.feedbackErrors = {...this.feedbackErrors, [toSend.turnId]: message};
                this.liveStatus = message;
                this.clearFeedbackSending(toSend.turnId);
            }
        })
    }

    public isFeedbackSending(turnId: string | null | undefined): boolean {
        return turnId != null && this.feedbackSending[turnId] === true;
    }

    private clearFeedbackSending(turnId: string): void {
        let { [turnId]: _cleared, ...rest } = this.feedbackSending;
        this.feedbackSending = rest;
    }

    public feedbackErrorFor(turnId: string | null | undefined): string {
        return turnId == null ? "" : (this.feedbackErrors[turnId] ?? "");
    }

    private clearFeedbackError(turnId: string): void {
        let { [turnId]: _cleared, ...rest } = this.feedbackErrors;
        this.feedbackErrors = rest;
    }

    public convertRatingStringToRatingNumber(toConvert:string):number {
        let converted:number = null;
        if(toConvert == this.negativeRating){
            converted = this.isNegative;
        }
        else if(toConvert == this.positiveRating){
            converted = this.isPositive;
        }
        return converted;
    }
}
